// src/Pages/RentalTerms/RentalTerms.jsx
import RentalTermsHelmetComponent from "./RentalTermsHelmetComponent";
import ResponsiveButton from "../../components/ResponsiveButton/ResponsiveButton";
import { MdArrowBack } from "react-icons/md";
import { useNavigate } from "react-router-dom";

const pageShellClass =
  "relative w-full overflow-hidden bg-gradient-to-br from-page via-surface2 to-surface3 px-4 py-24 text-[#111827] dark:from-darkpage dark:via-darksurface dark:to-darksurface2 dark:text-white sm:px-6 lg:px-8";

const contentCardClass =
  "relative mx-auto max-w-7xl overflow-hidden rounded-[2rem] border border-[#DDE7E5] bg-white/90 p-6 shadow-xl backdrop-blur-xl dark:border-darkborder dark:bg-[#111114]/95 sm:p-8 lg:p-10";

const badgeClass =
  "mb-5 inline-flex rounded-full border border-primary/40 bg-primary/10 px-4 py-1.5 font-customFont1 text-xs font-extrabold uppercase tracking-[0.14em] !text-[#111827] shadow-sm dark:!text-white";

const titleClass =
  "font-customFont2 text-5xl font-bold uppercase leading-none !text-[#111827] dark:!text-white sm:text-6xl lg:text-7xl";

const sectionTitleClass =
  "font-customFont2 text-3xl font-bold uppercase leading-none !text-[#111827] dark:!text-white sm:text-4xl";

const textClass =
  "mt-4 font-customFont1 text-lg leading-relaxed !text-[#374151] dark:!text-[#D1D5DB] sm:text-xl [&_strong]:font-extrabold [&_strong]:!text-[#111827] dark:[&_strong]:!text-white";

const sectionClass =
  "rounded-3xl border border-[#DDE7E5] bg-[#F8FAFC]/80 p-5 shadow-sm dark:border-darkborder dark:bg-darksurface/80 sm:p-6";

const infoBoxClass =
  "rounded-3xl border border-primary/30 bg-primary/10 p-5 dark:border-primary/30 dark:bg-primary/5";

const RentalTerms = () => {
  const navigate = useNavigate();

  return (
    <main className={pageShellClass}>
      <RentalTermsHelmetComponent />

      <div
        aria-hidden="true"
        className="
          pointer-events-none absolute left-1/2 top-16 h-72 w-72
          -translate-x-1/2 rounded-full bg-primary/15 blur-3xl
          dark:bg-primary/10
        "
      />

      <div
        aria-hidden="true"
        className="
          pointer-events-none absolute -right-24 top-96 h-96 w-96
          rounded-full bg-[#30f2cf]/10 blur-3xl
          dark:bg-primary/10
        "
      />

      <section className={contentCardClass} aria-labelledby="rental-terms-title">
        <div
          aria-hidden="true"
          className="
            pointer-events-none absolute -right-20 -top-20 h-56 w-56
            rounded-full bg-primary/10 blur-3xl
            dark:bg-primary/10
          "
        />

        <div className="relative">
          <ResponsiveButton
            onClick={() => {
              if (window.history.length > 2) {
                navigate(-1);
              } else {
                navigate("/");
              }

              setTimeout(() => {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }, 100);
            }}
            variant="backbutton"
            className="mb-8"
          >
            <div className="flex items-center gap-2">
              <MdArrowBack className="text-xl" />
              <span>Takaisin</span>
            </div>
          </ResponsiveButton>

          <span className={badgeClass}>Vuokrausehdot</span>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
            <div>
              <h1 id="rental-terms-title" className={titleClass}>
                Spiderlegs Productions vuokrausehdot
              </h1>

              <p className={`${textClass} max-w-4xl`}>
                Näitä ehtoja sovelletaan Spiderlegs Productionsin tarjoamien
                laitteiden, tapahtumatekniikan ja peräkärryn vuokraukseen.
                Varaamalla palveluita hyväksyt nämä ehdot.
              </p>
            </div>

            <aside className={infoBoxClass}>
              <p className="font-customFont1 text-sm font-extrabold uppercase tracking-[0.12em] !text-[#111827] dark:!text-white">
                Lyhyesti
              </p>

              <ul className="mt-4 space-y-3 font-customFont1 text-sm font-bold leading-relaxed !text-[#111827] dark:!text-white">
                <li className="flex gap-3">
                  <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" />
                  <span>Vuokraaja vastaa tuotteista koko vuokra-ajan.</span>
                </li>

                <li className="flex gap-3">
                  <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" />
                  <span>Tuotteita tulee käyttää huolellisesti ja ohjeiden mukaisesti.</span>
                </li>

                <li className="flex gap-3">
                  <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" />
                  <span>Peruutuksista ja muutoksista tulee ilmoittaa mahdollisimman ajoissa.</span>
                </li>
              </ul>
            </aside>
          </div>

          <div className="mt-10 space-y-6">
            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>1. Yleistä</h2>

              <p className={textClass}>
                Näitä vuokrausehtoja sovelletaan Spiderlegs Productionsin
                tarjoamien laitteiden, tapahtumatekniikan ja peräkärryjen
                vuokraukseen. Varaamalla tuotteita tai palveluita asiakas
                hyväksyy nämä ehdot.
              </p>
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>2. Varaaminen</h2>

              <p className={textClass}>
                Varaus tehdään verkkosivuston varauslomakkeen tai muun sovitun
                yhteydenottotavan kautta. Varaus katsotaan sitovaksi, kun
                asiakas on saanut varausvahvistuksen sähköpostiinsa ja
                mahdollinen varausmaksu on maksettu.
              </p>
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>3. Hinnat ja maksut</h2>

              <p className={textClass}>
                Vuokrahinnat ilmoitetaan verkkosivustolla tai erillisessä
                tarjouksessa. Varausmaksu voidaan edellyttää, ja se vähennetään
                kokonaishinnasta. Loppumaksu suoritetaan sovitun aikataulun
                mukaisesti.
              </p>
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>4. Peruutus ja muutokset</h2>

              <p className={textClass}>
                Varaus voidaan peruuttaa ilmoittamalla siitä viipymättä
                Spiderlegs Productionsille. Varausmaksua ei palauteta, ellei
                toisin erikseen sovita. Muutokset varaukseen pyritään
                toteuttamaan mahdollisuuksien mukaan.
              </p>
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>5. Vastuut ja vahingot</h2>

              <p className={textClass}>
                Vuokraaja vastaa vuokratuotteista koko vuokra-ajan.
                Vuokraaja on velvollinen korvaamaan kadonneet, rikkoutuneet tai
                vahingoittuneet laitteet sekä mahdolliset puhdistus-,
                korjaus- tai jälleenhankintakulut.
              </p>
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>6. Laitteiden käyttö</h2>

              <p className={textClass}>
                Vuokrattuja laitteita tulee käyttää huolellisesti ja annettujen
                ohjeiden mukaisesti. Laitteita ei saa luovuttaa kolmannelle
                osapuolelle ilman vuokranantajan lupaa. Vuokraaja vastaa siitä,
                että laitteita käytetään turvallisesti ja käyttötarkoituksen
                mukaisesti.
              </p>
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>7. Peräkärryn vuokraus</h2>

              <p className={textClass}>
                Peräkärryn vuokraaja vastaa siitä, että vetoauto, ajokorttiluokka
                ja kuorman sitominen ovat lain ja turvallisuusvaatimusten
                mukaisia. Peräkärryä tulee käyttää tavanomaiseen kuljetukseen,
                eikä sitä saa käyttää esimerkiksi maa-ainesten, kuten soran,
                hiekan tai mullan, kuljettamiseen ilman erillistä lupaa.
              </p>

              <p className={textClass}>
                Peräkärry tulee palauttaa sovittuna ajankohtana,
                puhdistettuna ja samassa kunnossa kuin se oli noudettaessa.
                Vuokraaja vastaa vuokra-aikana aiheutuneista vahingoista,
                puhdistustarpeista, sakoista ja kuljettamastaan kuormasta.
              </p>
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>8. Myöhästyminen ja palautus</h2>

              <p className={textClass}>
                Vuokratuotteet tulee palauttaa sovittuna aikana. Myöhästymisestä
                voi aiheutua lisämaksu, erityisesti jos viivästys vaikuttaa
                seuraavaan varaukseen tai aiheuttaa muuta haittaa.
              </p>
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>9. Palvelun saatavuus</h2>

              <p className={textClass}>
                Spiderlegs Productions pyrkii varmistamaan, että varatut
                tuotteet ovat käytettävissä sovittuna ajankohtana. Mikäli
                tuote ei ole saatavilla esimerkiksi teknisen vian tai muun
                yllättävän syyn vuoksi, pyrimme tarjoamaan korvaavan ratkaisun
                tai sopimaan asiakkaan kanssa muusta menettelystä.
              </p>
            </section>

            <section className={sectionClass}>
              <h2 className={sectionTitleClass}>10. Sovellettava laki</h2>

              <p className={textClass}>
                Näihin ehtoihin sovelletaan Suomen lakia. Mahdolliset
                erimielisyydet pyritään ratkaisemaan ensisijaisesti
                neuvottelemalla.
              </p>
            </section>

            <section className={infoBoxClass}>
              <h2 className={sectionTitleClass}>Yhteystiedot</h2>

              <p className={textClass}>
                Vuokrausehtoihin liittyvissä kysymyksissä voit olla yhteydessä:
              </p>

              <p className="mt-4 font-customFont1 text-lg font-bold leading-relaxed !text-[#111827] dark:!text-white sm:text-xl">
                Spiderlegs Productions
                <br />
                Arttu Stenius
                <br />
                +358 44 351 92 65
                <br />
                <a
                  href="mailto:info@spiderlegsproductions.com"
                  className="
                    font-extrabold !text-[#111827] underline decoration-primary
                    decoration-2 underline-offset-4 transition hover:!text-primary
                    dark:!text-white dark:hover:!text-primary
                  "
                >
                  info@spiderlegsproductions.com
                </a>
              </p>
            </section>
          </div>
        </div>
      </section>
    </main>
  );
};

export default RentalTerms;